import { useState, useEffect, useRef, useCallback } from "react";
import { axiosRequest } from "../../utility/axios";

const POLL_MS = 3000;
const COLORS = [
  "#FFD700","#FF6B6B","#4ECDC4","#45B7D1","#96CEB4",
  "#FFEAA7","#DDA0DD","#FF4500","#00FF7F","#FF69B4","#00CED1","#FFA500",
];

function playFanfare(actx) {
  const now = actx.currentTime;
  // Rising arpeggio melody
  const melody = [
    [523.25, 0.00, 0.20],
    [659.25, 0.18, 0.20],
    [783.99, 0.36, 0.20],
    [1046.5, 0.54, 0.60],
    [783.99, 0.72, 0.14],
    [1046.5, 0.86, 0.14],
    [1318.5, 1.00, 1.10],
  ];
  melody.forEach(([freq, offset, dur]) => {
    const osc = actx.createOscillator();
    const env = actx.createGain();
    osc.connect(env); env.connect(actx.destination);
    osc.type = "sine"; osc.frequency.value = freq;
    const s = now + offset;
    env.gain.setValueAtTime(0.001, s);
    env.gain.linearRampToValueAtTime(0.32, s + 0.04);
    env.gain.exponentialRampToValueAtTime(0.001, s + dur);
    osc.start(s); osc.stop(s + dur + 0.1);
  });
  // Harmony chord
  [523.25, 659.25, 783.99].forEach(freq => {
    const osc = actx.createOscillator();
    const env = actx.createGain();
    osc.connect(env); env.connect(actx.destination);
    osc.type = "triangle"; osc.frequency.value = freq;
    const s = now + 0.54;
    env.gain.setValueAtTime(0.001, s);
    env.gain.linearRampToValueAtTime(0.1, s + 0.05);
    env.gain.exponentialRampToValueAtTime(0.001, s + 1.2);
    osc.start(s); osc.stop(s + 1.3);
  });
  // Coin jingle
  [2000, 2200, 1800, 2400, 1600, 2600].forEach((freq, i) => {
    const osc = actx.createOscillator();
    const env = actx.createGain();
    osc.connect(env); env.connect(actx.destination);
    osc.type = "sine"; osc.frequency.value = freq;
    const s = now + 0.06 + i * 0.09;
    env.gain.setValueAtTime(0.001, s);
    env.gain.linearRampToValueAtTime(0.18, s + 0.01);
    env.gain.exponentialRampToValueAtTime(0.001, s + 0.12);
    osc.start(s); osc.stop(s + 0.15);
  });
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@900&family=Cinzel:wght@700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

.bbj-root{
  position:fixed;inset:0;
  background:radial-gradient(ellipse at 30% 40%,#0a1628 0%,#000814 55%,#000000 100%);
  display:flex;flex-direction:column;align-items:center;justify-content:center;
  overflow:hidden;font-family:'Cinzel','Georgia',serif;
  cursor:pointer;user-select:none;
}
.bbj-canvas{position:fixed;inset:0;pointer-events:none;z-index:10;}

/* Ambient orbs */
.bbj-orb{position:absolute;border-radius:50%;filter:blur(100px);pointer-events:none;}
.bbj-orb-1{width:800px;height:800px;background:radial-gradient(circle,rgba(255,215,0,.1) 0%,transparent 65%);top:-300px;left:-300px;animation:orb-drift 14s ease-in-out infinite;}
.bbj-orb-2{width:600px;height:600px;background:radial-gradient(circle,rgba(255,80,0,.09) 0%,transparent 65%);bottom:-200px;right:-200px;animation:orb-drift 11s ease-in-out infinite reverse;}
.bbj-orb-3{width:500px;height:500px;background:radial-gradient(circle,rgba(120,0,255,.07) 0%,transparent 65%);top:50%;left:50%;transform:translate(-50%,-50%);animation:orb-drift 18s ease-in-out infinite 4s;}
@keyframes orb-drift{0%,100%{transform:translate(0,0) scale(1);}33%{transform:translate(40px,-25px) scale(1.06);}66%{transform:translate(-25px,35px) scale(0.96);}}

/* Corner brackets */
.bbj-corner{position:fixed;width:70px;height:70px;border-color:rgba(255,215,0,.35);border-style:solid;pointer-events:none;z-index:5;animation:corner-pulse 3s ease-in-out infinite;}
.bbj-corner.tl{top:18px;left:18px;border-width:2px 0 0 2px;}
.bbj-corner.tr{top:18px;right:18px;border-width:2px 2px 0 0;}
.bbj-corner.bl{bottom:18px;left:18px;border-width:0 0 2px 2px;}
.bbj-corner.br{bottom:18px;right:18px;border-width:0 2px 2px 0;}
@keyframes corner-pulse{0%,100%{border-color:rgba(255,215,0,.25);}50%{border-color:rgba(255,215,0,.75);}}

/* Content */
.bbj-content{position:relative;z-index:5;display:flex;flex-direction:column;align-items:center;gap:6px;padding:0 24px;text-align:center;}

.bbj-club-name{font-size:clamp(13px,2.2vw,22px);letter-spacing:8px;color:rgba(255,215,0,.6);text-transform:uppercase;animation:fade-glow 3.5s ease-in-out infinite;}

.bbj-trophy{font-size:clamp(44px,8vw,100px);animation:trophy-float 3.2s ease-in-out infinite;filter:drop-shadow(0 0 25px rgba(255,215,0,.9));line-height:1;display:block;}
@keyframes trophy-float{0%,100%{transform:translateY(0) rotate(-4deg);}50%{transform:translateY(-14px) rotate(4deg);}}

.bbj-title{
  font-family:'Cinzel Decorative','Georgia',serif;
  font-size:clamp(22px,5.5vw,72px);font-weight:900;
  background:linear-gradient(90deg,#9a6f00,#FFD700,#FFEC8B,#FFA500,#FFD700,#9a6f00);
  background-size:300%;
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
  animation:shimmer 4s linear infinite,title-glow 2.5s ease-in-out infinite;
  letter-spacing:2px;line-height:1.15;
}
@keyframes shimmer{0%{background-position:0% center;}100%{background-position:300% center;}}
@keyframes title-glow{0%,100%{filter:drop-shadow(0 0 8px rgba(255,215,0,.4));}50%{filter:drop-shadow(0 0 30px rgba(255,165,0,.9));}}

.bbj-diamonds{color:rgba(255,215,0,.45);font-size:clamp(10px,1.6vw,16px);letter-spacing:10px;animation:fade-glow 3s ease-in-out infinite;}

.bbj-divider{
  width:clamp(160px,58vw,620px);height:1px;
  background:linear-gradient(90deg,transparent,rgba(255,215,0,.2) 10%,#FFD700 30%,#FFA500 50%,#FFD700 70%,rgba(255,215,0,.2) 90%,transparent);
  margin:2px 0;animation:fade-glow 2.5s ease-in-out infinite;
}

.bbj-amount-block{display:flex;align-items:baseline;gap:8px;margin:4px 0;}

.bbj-rupee{
  font-family:'Cinzel Decorative','Georgia',serif;
  font-size:clamp(30px,7vw,96px);font-weight:900;color:#FFD700;
  line-height:1;margin-top:0.1em;
  filter:drop-shadow(0 0 20px rgba(255,215,0,.8));
}

.bbj-amount{
  font-family:'Cinzel Decorative','Georgia',serif;
  font-size:clamp(58px,15vw,200px);font-weight:900;color:#FFD700;
  letter-spacing:-2px;line-height:1;
  animation:amount-glow 2s ease-in-out infinite;
}
@keyframes amount-glow{
  0%,100%{filter:drop-shadow(0 0 20px rgba(255,215,0,.6)) drop-shadow(0 0 50px rgba(255,165,0,.25));}
  50%{filter:drop-shadow(0 0 50px rgba(255,215,0,1)) drop-shadow(0 0 100px rgba(255,165,0,.6));}
}
.bbj-amount--bang{animation:amount-bang .65s cubic-bezier(.36,.07,.19,.97) both,amount-glow 2s ease-in-out infinite!important;}
@keyframes amount-bang{
  0%{transform:scale(1);color:#FFD700;}
  15%{transform:scale(1.22);color:#fff;filter:brightness(3);}
  35%{transform:scale(0.93);}
  55%{transform:scale(1.1);}
  75%{transform:scale(0.97);}
  100%{transform:scale(1);color:#FFD700;}
}

.bbj-live-row{display:flex;align-items:center;gap:20px;margin-top:4px;}
.bbj-live{display:inline-flex;align-items:center;gap:7px;border:1px solid rgba(255,60,60,.45);background:rgba(255,30,30,.1);border-radius:100px;padding:5px 18px 5px 14px;color:#ff6b6b;font-size:clamp(10px,1.5vw,15px);letter-spacing:4px;}
.bbj-live-dot{width:8px;height:8px;border-radius:50%;background:#ff3333;box-shadow:0 0 8px #ff3333;animation:live-blink 1.2s step-start infinite;}
@keyframes live-blink{0%,100%{opacity:1;}50%{opacity:0;}}

.bbj-update-note{font-size:clamp(9px,1.3vw,13px);color:rgba(255,215,0,.3);letter-spacing:2px;}

.bbj-sound-hint{margin-top:10px;font-size:clamp(10px,1.5vw,14px);color:rgba(255,215,0,.3);letter-spacing:2px;animation:fade-glow 3s ease-in-out infinite;}

@keyframes fade-glow{0%,100%{opacity:.55;}50%{opacity:1;}}

/* Screen flash */
.bbj-flash{position:fixed;inset:0;z-index:8;pointer-events:none;background:rgba(255,215,0,.18);animation:flash-out .9s ease-out forwards;}
@keyframes flash-out{0%{opacity:1;}100%{opacity:0;}}

/* Bang overlay */
.bbj-bang-wrap{position:fixed;inset:0;z-index:20;display:flex;align-items:center;justify-content:center;pointer-events:none;}
.bbj-bang-box{
  background:rgba(0,0,0,.78);border:3px solid #FFD700;border-radius:22px;
  padding:28px 50px;text-align:center;
  backdrop-filter:blur(14px);
  box-shadow:0 0 80px rgba(255,215,0,.45),0 0 160px rgba(255,165,0,.2);
  animation:bang-in .55s cubic-bezier(.34,1.56,.64,1);
  max-width:90vw;
}
.bbj-bang-line1{
  font-family:'Cinzel Decorative','Georgia',serif;
  font-size:clamp(28px,7vw,88px);color:#FFD700;font-weight:900;
  line-height:1.2;text-shadow:0 0 30px rgba(255,215,0,.9);
}
.bbj-bang-line2{font-size:clamp(14px,2.5vw,26px);color:rgba(255,215,0,.65);letter-spacing:4px;margin-top:8px;}
@keyframes bang-in{0%{transform:scale(.25) rotate(-10deg);opacity:0;}60%{transform:scale(1.07) rotate(2deg);}100%{transform:scale(1) rotate(0deg);opacity:1;}}

/* Footer */
.bbj-footer{position:fixed;bottom:14px;color:rgba(255,215,0,.18);font-size:clamp(9px,1vw,12px);letter-spacing:6px;text-transform:uppercase;z-index:5;pointer-events:none;}
`;

export default function BBJDisplay() {
  const [displayed, setDisplayed]   = useState(0);
  const [celebrating, setCelebrating] = useState(false);
  const [bangVisible, setBangVisible] = useState(false);
  const [soundHint, setSoundHint]   = useState(true);

  const currentRef   = useRef(null);
  const canvasRef    = useRef(null);
  const particlesRef = useRef([]);
  const starsRef     = useRef([]);
  const rafRef       = useRef(null);
  const audioRef     = useRef(null);

  // ── CSS + font injection ──────────────────────────────────────────────────
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@900&family=Cinzel:wght@700&display=swap";
    link.id = "bbj-fonts";
    document.head.appendChild(link);
    const style = document.createElement("style");
    style.id = "bbj-css";
    style.textContent = CSS;
    document.head.appendChild(style);
    return () => {
      document.getElementById("bbj-fonts")?.remove();
      document.getElementById("bbj-css")?.remove();
    };
  }, []);

  // ── Stars ────────────────────────────────────────────────────────────────
  useEffect(() => {
    starsRef.current = Array.from({ length: 220 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.8 + 0.3,
      a: Math.random(),
      da: (Math.random() * 0.012 + 0.002) * (Math.random() > 0.5 ? 1 : -1),
    }));
  }, []);

  // ── Canvas loop ──────────────────────────────────────────────────────────
  useEffect(() => {
    const cv  = canvasRef.current;
    const ctx = cv.getContext("2d");
    const resize = () => { cv.width = window.innerWidth; cv.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);

    const tick = () => {
      ctx.clearRect(0, 0, cv.width, cv.height);

      // twinkling stars
      starsRef.current.forEach(s => {
        s.a += s.da;
        if (s.a <= 0 || s.a >= 1) s.da *= -1;
        ctx.save();
        ctx.globalAlpha = Math.max(0, Math.min(1, s.a));
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.arc(s.x * cv.width, s.y * cv.height, s.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      // particles (confetti + fireworks)
      particlesRef.current = particlesRef.current.filter(p => p.a > 0.01);
      for (const p of particlesRef.current) {
        p.x += p.vx; p.y += p.vy;
        p.vy += p.grav ?? 0.15;
        p.vx *= p.drag ?? 0.99;
        if (p.rV) p.r0 = (p.r0 || 0) + p.rV;
        p.a -= p.dec ?? 0.01;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.a);
        ctx.fillStyle = p.color;
        if (p.isRect) {
          ctx.translate(p.x, p.y);
          ctx.rotate(p.r0 || 0);
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.sz ?? 3, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // ── Spawners ─────────────────────────────────────────────────────────────
  const spawnConfetti = () => {
    const ps = [];
    for (let i = 0; i < 380; i++) {
      ps.push({
        isRect: true,
        x: Math.random() * window.innerWidth,
        y: -30 - Math.random() * 500,
        vx: (Math.random() - 0.5) * 11,
        vy: Math.random() * 5 + 1.5,
        r0: Math.random() * Math.PI * 2,
        rV: (Math.random() - 0.5) * 0.24,
        w: Math.random() * 15 + 7,
        h: Math.random() * 9 + 4,
        color: COLORS[~~(Math.random() * COLORS.length)],
        a: 1, grav: 0.1, drag: 0.99, dec: 0.0028,
      });
    }
    particlesRef.current.push(...ps);
  };

  const spawnFirework = useCallback((x, y) => {
    const ps = [];
    const N = 130;
    for (let i = 0; i < N; i++) {
      const ang = (i / N) * Math.PI * 2;
      const spd = Math.random() * 13 + 5;
      ps.push({
        x, y,
        vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd,
        sz: Math.random() * 5 + 2,
        color: COLORS[~~(Math.random() * COLORS.length)],
        a: 1, grav: 0.07, drag: 0.95, dec: 0.013,
      });
    }
    // white sparks
    for (let i = 0; i < 60; i++) {
      ps.push({
        x: x + (Math.random() - 0.5) * 30,
        y: y + (Math.random() - 0.5) * 30,
        vx: (Math.random() - 0.5) * 7,
        vy: (Math.random() - 0.5) * 7,
        sz: Math.random() * 2.5 + 0.5,
        color: "#fff",
        a: 1, grav: 0.04, drag: 0.97, dec: 0.018,
      });
    }
    particlesRef.current.push(...ps);
  }, []);

  // ── Counter animation ────────────────────────────────────────────────────
  const animCount = (from, to) => {
    const dur = 2500, t0 = Date.now();
    const step = () => {
      const p = Math.min((Date.now() - t0) / dur, 1);
      const e = 1 - Math.pow(1 - p, 4);
      setDisplayed(Math.round(from + (to - from) * e));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  // ── Celebrate ────────────────────────────────────────────────────────────
  const celebrate = useCallback((oldVal, newVal) => {
    const ac = audioRef.current;
    if (ac) {
      // resume() resolves immediately if already running; unlocks if suspended
      ac.resume().then(() => playFanfare(ac)).catch(() => {});
    }

    setCelebrating(true);
    setBangVisible(true);
    setTimeout(() => setCelebrating(false), 5000);
    setTimeout(() => setBangVisible(false), 4500);

    spawnConfetti();

    const spots = [
      [window.innerWidth * 0.1,  window.innerHeight * 0.2],
      [window.innerWidth * 0.9,  window.innerHeight * 0.2],
      [window.innerWidth * 0.5,  window.innerHeight * 0.08],
      [window.innerWidth * 0.2,  window.innerHeight * 0.5],
      [window.innerWidth * 0.8,  window.innerHeight * 0.5],
      [window.innerWidth * 0.5,  window.innerHeight * 0.42],
      [window.innerWidth * 0.3,  window.innerHeight * 0.3],
      [window.innerWidth * 0.7,  window.innerHeight * 0.3],
      [window.innerWidth * 0.15, window.innerHeight * 0.65],
      [window.innerWidth * 0.85, window.innerHeight * 0.65],
    ];
    spots.forEach(([x, y], i) => setTimeout(() => spawnFirework(x, y), i * 120));

    animCount(oldVal, newVal);
  }, [spawnFirework]);

  // ── Audio init on first click ────────────────────────────────────────────
  const initAudio = () => {
    try {
      if (!audioRef.current) {
        audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      // Always resume inside user gesture — unlocks on Safari/Chrome regardless of state
      audioRef.current.resume().then(() => setSoundHint(false)).catch(() => {});
    } catch {}
  };

  // ── Poll ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const poll = async () => {
      try {
        const r   = await axiosRequest("GET", `${process.env.REACT_APP_API_URL}/bbj/public`, null);
        const amt = r?.data?.data?.bbj_amount;
        if (amt == null) return;
        if (currentRef.current === null) {
          currentRef.current = amt;
          setDisplayed(amt);
          return;
        }
        if (amt > currentRef.current) {
          const old = currentRef.current;
          currentRef.current = amt;
          celebrate(old, amt);
        } else if (amt !== currentRef.current) {
          currentRef.current = amt;
          setDisplayed(amt);
        }
      } catch {}
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [celebrate]);

  const fmt = n => Number(n).toLocaleString("en-IN");

  return (
    <div className="bbj-root" onClick={initAudio}>
      <canvas ref={canvasRef} className="bbj-canvas" />

      {/* Ambient background orbs */}
      <div className="bbj-orb bbj-orb-1" />
      <div className="bbj-orb bbj-orb-2" />
      <div className="bbj-orb bbj-orb-3" />

      {/* Corner decorations */}
      <div className="bbj-corner tl" />
      <div className="bbj-corner tr" />
      <div className="bbj-corner bl" />
      <div className="bbj-corner br" />

      {/* Main content */}
      <div className="bbj-content">
        <div className="bbj-club-name">♠ Real Club ♠</div>
        <span className="bbj-trophy">🏆</span>
        <h1 className="bbj-title">Bad Beat Jackpot</h1>
        <div className="bbj-diamonds">◆ &nbsp; ◆ &nbsp; ◆ &nbsp; ◆ &nbsp; ◆</div>
        <div className="bbj-divider" />
        <div className="bbj-amount-block">
          <span className="bbj-rupee"></span>
          <span className={`bbj-amount${celebrating ? " bbj-amount--bang" : ""}`}>
            {fmt(displayed)}
          </span>
        </div>
        <div className="bbj-divider" />
        <div className="bbj-live-row">
          <div className="bbj-live">
            <div className="bbj-live-dot" />
            <span>LIVE</span>
          </div>
          <div className="bbj-update-note">Auto-updates every few seconds</div>
        </div>
        {soundHint && (
          <div className="bbj-sound-hint">🔊 Tap anywhere to enable celebration sound</div>
        )}
      </div>

      {/* Screen flash on jackpot increase */}
      {celebrating && <div className="bbj-flash" />}

      {/* JACKPOT UPDATED banner */}
      {bangVisible && (
        <div className="bbj-bang-wrap">
          <div className="bbj-bang-box">
            <div className="bbj-bang-line1">🎉 JACKPOT UPDATED! 🎉</div>
            <div className="bbj-bang-line2">The Pool Has Grown!</div>
          </div>
        </div>
      )}

      <div className="bbj-footer">♠ ♥ &nbsp; Real Club · Bad Beat Jackpot · Live &nbsp; ♦ ♣</div>
    </div>
  );
}
